import React from 'react';
import { groupBy, sum, omit } from 'lodash';

import { useTranslation } from '@lt/localization';

import type { IHotelAirport } from 'checkout/store/hotel/types';
import {
  Experiment,
  EXPERIMENT_NAMES,
  Variant,
} from 'src/components/Experiment';

import Summary from '../Summary';
import { EXTRA_SCOPES, EXTRAS } from '../../../store/constants/extras';
import FlightSummaryItem from './components/FlightSummaryItem';
import PriceDetails from '../../PriceDetails';
import OperatorDetails from '../OperatorDetails';
import type { Item } from '../types';

import FlightCashback from '../FlightCashback';
import type { ExtraItem } from '../../../types/extras';
import type { TransformedBackFlight } from '../../../CheckoutRoutes/PackageCheckout/types/flights';
import TransferMedInsuranceInfo from '../../TransferMedInsuranceInfo';
import ManagerInfo from '../components/ManagerInfo';

import styles from './flightSummary.module.css';

interface FlightSummaryProps {
  packagePrice: number;
  fuelCharge?: number;
  extras?: ExtraItem[];
  instantConfirm?: boolean;
  isMobile: boolean;
  tourId: TransformedBackFlight['tour_id'];
  transfer: boolean;
  insurance: boolean;
  airports: IHotelAirport[];
  transferPrivate: boolean;
}

const FlightSummary = ({
  packagePrice,
  fuelCharge = 0,
  extras = [],
  instantConfirm,
  tourId,
  transfer,
  insurance,
  transferPrivate,
  airports,
  isMobile,
}: FlightSummaryProps) => {
  const { t } = useTranslation();

  const fullPrice =
    packagePrice +
    fuelCharge +
    sum(extras && extras.map((extra) => extra.price));

  const extrasWithoutIcons = extras.map((item) => omit(item, 'icon'));
  const items: Item[] = [
    { name: t('checkout.flight.cost'), price: packagePrice },
    {
      name: t('checkout.flight.item.surcharge'),
      price: fuelCharge,
      scope: EXTRA_SCOPES.FLIGHT,
    },
    ...extrasWithoutIcons,
  ];

  const { flightExtras, nonFlightExtras } = groupBy(
    items,
    ({ scope, code_name }) =>
      scope === EXTRA_SCOPES.FLIGHT && code_name !== EXTRAS.FLIGHT_LOCK
        ? 'flightExtras'
        : 'nonFlightExtras',
  );

  return (
    <>
      <div className={styles.header}>
        <Summary value={fullPrice} instantConfirm={instantConfirm} />
        <FlightCashback tourId={tourId} fullPrice={fullPrice} />
      </div>
      <div className={styles.priceDetails}>
        <PriceDetails>
          {nonFlightExtras.map((item) => {
            const tooltipText =
              item.code_name === EXTRAS.FLIGHT_LOCK
                ? t('checkout.flight.flightLock')
                : undefined;

            return (
              <FlightSummaryItem
                key={item.name}
                tooltipText={tooltipText}
                {...item}
              />
            );
          })}
        </PriceDetails>
        <PriceDetails>
          {flightExtras.map((item) => {
            const name =
              item.code_name === EXTRAS.INFANT_BOARDING_PASS
                ? t('checkout.upgradeFlightClass.infant')
                : item.name;

            return <FlightSummaryItem key={item.name} {...item} name={name} />;
          })}
        </PriceDetails>
      </div>
      <TransferMedInsuranceInfo
        sidebar={false}
        transfer={transfer}
        insurance={insurance}
        airports={airports}
        transferPrivate={transferPrivate}
        isMobile={isMobile}
      />
      <Experiment name={EXPERIMENT_NAMES.ACTUALIZE_ALTERNATIVES.NAME}>
        <Variant option={EXPERIMENT_NAMES.ACTUALIZE_ALTERNATIVES.OPTIONS.ON}>
          <OperatorDetails tourId={tourId} />
        </Variant>
      </Experiment>
      <ManagerInfo tourId={tourId} />
    </>
  );
};

export default FlightSummary;
